import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  getDeletedQAPaginated,
  searchDeletedQA,
  restoreDeletedQA,
  editQAPoint,
  softDeleteQa,
  deleteQAPoint, // permanent delete if needed
} from "../../Api_Qdrant/Flask_Apis";
import Linkify from "linkify-react";
import { toast } from "react-toastify";
import "./Show_DeletedQdrantQa.css"; // reuse same CSS
import "../../app/components/Pagination/Pagination.css";
import searchSvg from "../../assets/icons/search.svg";
import deleteSvg from "../../assets/icons/delete.svg";
import restoreSvg from "../../assets/icons/restore.svg";
import editSvg from "../../assets/icons/edit-img.svg";
import ConfirmModal from "../../app/components/WarningModel/ConfirmModel";
export default function Show_DeletedQdrantQa() {
  const [qaList, setQaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingBatch, setLoadingBatch] = useState(false);

  const [totalPoints, setTotalPoints] = useState(0);

  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  // Bulk operations states
  const [bulkRestoreMode, setBulkRestoreMode] = useState(false);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Client-side pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const bottomRef = useRef(null);
  const nextOffsetRef = useRef(null);
  const [showWarningModal, setshowWarningModal] = useState(false);
  const [showWarningModalForBulkDelete, setshowWarningModalForBulkDelete] =
    useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({
    question: "",
    answer: "",
    is_deleted: true,
  });
  const [qAIdToDelete, setqAIdToDelete] = useState(null);
  const collection = "CustomAi";
  const pageSize = 200;

  const linkifyOptions = {
    target: "_blank",
    rel: "noopener noreferrer",
    className: "qa-link",
  };

  // Fetch deleted QAs
  useEffect(() => {
    fetchDeleted(true);
  }, []);

  const fetchDeleted = useCallback(
    async (reset = false) => {
      try {
        if (reset) {
          setLoading(true);
          setQaList([]);
          setTotalPoints(0);
          nextOffsetRef.current = null;
          setCurrentPage(1);

          // Load first batch immediately
          const firstBatchData = await getDeletedQAPaginated(
            collection,
            pageSize,
            null,
          );

          if (firstBatchData.success) {
            const result = firstBatchData.result || {};
            const firstBatchItems = result.deleted_qa_list || [];
            nextOffsetRef.current = result.next_page_offset || null;
            const totalDeletedPoints = result.deleted_points_count || 0;
            setTotalPoints(totalDeletedPoints);

            // Show first batch immediately
            setQaList(firstBatchItems);
            setLoading(false);

            // Load remaining batches in background if more data available
            if (nextOffsetRef.current && firstBatchItems.length > 0) {
              // Async function to load remaining batches without blocking UI
              const loadRemainingBatches = async () => {
                try {
                  let totalLoaded = firstBatchItems.length;

                  // Continue loading until we reach total deleted QA points from backend
                  while (totalLoaded < totalDeletedPoints) {
                    const data = await getDeletedQAPaginated(
                      collection,
                      pageSize,
                      nextOffsetRef.current,
                    );

                    if (!data.success) break;

                    const batchItems = data.result?.deleted_qa_list || [];
                    if (batchItems.length === 0) break;

                    totalLoaded += batchItems.length;
                    nextOffsetRef.current =
                      data.result.next_page_offset || null;

                    // Update UI with new items
                    setQaList((prev) => [...prev, ...batchItems]);

                    // Stop if no more data
                    if (!nextOffsetRef.current) break;

                    // Wait 1 second before fetching next batch
                    if (
                      totalLoaded < totalDeletedPoints &&
                      nextOffsetRef.current
                    ) {
                      await new Promise((resolve) => setTimeout(resolve, 1000));
                    }
                  }
                } catch (err) {
                  console.error("Error loading remaining batches:", err);
                }
              };

              // Start loading remaining batches without awaiting
              loadRemainingBatches();
            }
          } else {
            toast.error(firstBatchData.error || "Failed to load deleted Q&A");
            setLoading(false);
          }
        }
      } catch (err) {
        toast.error("Failed to load deleted Q&A");
        setLoading(false);
      } finally {
        setSearchLoading(false);
      }
    },
    [collection, pageSize],
  );

  // Edit modal
  const openModal = (item) => {
    setEditingItem(item);
    setEditForm({
      question: item.question,
      answer: item.answer,
      is_deleted: item.is_deleted,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setEditingItem(null);
    setEditForm({ question: "", answer: "" });
    setShowModal(false);
  };

  // ---------- Client-side Pagination ----------
  const generatePageSizeOptions = () => {
    if (totalPoints <= 0) return [5, 10, 20, 25];

    // Generate 4 options based on total points
    const quarter = Math.floor(totalPoints / 4);
    const half = Math.floor(totalPoints / 2);
    const threeQuarters = Math.floor((totalPoints * 3) / 4);

    // Create array and remove duplicates
    return [quarter, half, threeQuarters, totalPoints]
      .filter((v, i, arr) => v > 0 && arr.indexOf(v) === i)
      .sort((a, b) => a - b);
  };

  const getPaginatedData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return qaList.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(qaList.length / itemsPerPage);
  };

  const handlePageChange = (pageNum) => {
    if (!loading && !loadingBatch) {
      setCurrentPage(pageNum);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleItemsPerPageChange = (newSize) => {
    setItemsPerPage(parseInt(newSize));
    setCurrentPage(1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1 && !loading && !loadingBatch) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < getTotalPages() && !loading && !loadingBatch) {
      handlePageChange(currentPage + 1);
    }
  };

  // ---------- Generate page numbers for pagination ----------
  const generatePageNumbers = () => {
    const totalPages = getTotalPages();
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= maxVisible; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - maxVisible + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const saveEdit = async () => {
    try {
      await editQAPoint(
        collection,
        editingItem.id,
        editForm.question,
        editForm.answer,
        editForm.is_deleted,
      );
      if (isSearching) {
        await handleSearchChange(searchText);
      } else {
        await fetchDeleted(true);
      }

      toast.success("Updated successfully");
      closeModal();
    } catch (err) {
      toast.error("Updation failed");
    }
  };

  // Restore function with better error handling
  const restoreItem = async (id) => {
    try {
      const result = await restoreDeletedQA(collection, [id]);

      if (result.error) {
        toast.error(`Restore failed: ${result.message}`);
        console.error("Restore details:", result.details);
        return;
      }

      toast.success("Restored successfully!");

      // Refresh data
      if (isSearching) {
        await handleSearchChange(searchText);
      } else {
        await fetchDeleted(true);
      }
    } catch (err) {
      console.error("Unexpected error in restoreItem:", err);
      toast.error("Restore failed due to unexpected error");
    }
  };

  // Permanently delete (optional)
  const permanentDelete = async (id) => {
    try {
      await deleteQAPoint(collection, [id]);
      if (isSearching) await handleSearchChange(searchText);
      else await fetchDeleted(true);

      toast.success("Q&A deleted permanently");
    } catch (err) {
      toast.error("Failed to delete Q&A permanently");
    }
  };

  // Bulk restore
  const handleBulkRestore = async () => {
    if (selectedIds.length === 0) return;
    try {
      const result = await restoreDeletedQA(collection, selectedIds);

      if (result.error) {
        toast.error(`Bulk restore failed: ${result.message}`);
        return;
      }

      toast.success(
        `Successfully restored ${selectedIds.length} ${selectedIds.length > 1 ? "questions" : "question"}`,
      );

      // Optimistic update
      setQaList((prev) => prev.filter((q) => !selectedIds.includes(q.id)));
      setSearchResults((prev) =>
        prev.filter((q) => !selectedIds.includes(q.id)),
      );

      // Reset states
      setSelectedIds([]);
      setSelectAll(false);
      setBulkRestoreMode(false);
    } catch (err) {
      console.error("Bulk restore failed:", err);
      toast.error("Bulk restore failed");
    }
  };

  // Bulk permanent delete
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    try {
      var result = await deleteQAPoint(collection, selectedIds);
      if (result.success || result.status === "ok") {
        toast.success(
          `Successfully permanently deleted ${selectedIds.length} ${selectedIds.length > 1 ? "questions" : "question"}`,
        );

        // Optimistic update
        setQaList((prev) => prev.filter((q) => !selectedIds.includes(q.id)));
        setSearchResults((prev) =>
          prev.filter((q) => !selectedIds.includes(q.id)),
        );

        // Reset states
        setSelectedIds([]);
        setSelectAll(false);
        setBulkDeleteMode(false);
      } else {
        console.error("Bulk delete API returned error:", result);
        toast.error(
          result.message ||
            result.error ||
            `Failed to delete ${selectedIds.length > 1 ? "questions" : "question"}`,
        );
      }
    } catch (err) {
      console.error("Bulk delete failed:", err);
      toast.error("Bulk delete failed");
    }
  };

  // Toggle select all
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
    } else {
      const allIds = isSearching
        ? searchResults.map((item) => item.id)
        : qaList.map((item) => item.id);
      setSelectedIds(allIds);
    }
    setSelectAll(!selectAll);
  };

  // Handle individual checkbox change
  const handleCheckboxChange = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((itemId) => itemId !== id)
        : [...prev, id],
    );
  };

  // Clear all selections
  const clearSelections = () => {
    setSelectedIds([]);
    setSelectAll(false);
  };

  // Exit bulk mode
  const exitBulkMode = () => {
    setBulkRestoreMode(false);
    setBulkDeleteMode(false);
    clearSelections();
  };

  // Debounce search
  const debounce = (fn, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  };

  const debouncedSearch = useRef(
    debounce(async (text) => {
      try {
        setSearchLoading(true);

        const res = await searchDeletedQA(text);

        // check API success
        if (!res?.success) {
          console.error("Search error:", res?.error);
          setSearchResults([]);
          return;
        }

        const points = res.result?.deleted_points || [];

        if (points.length > 0) {
          console.log(points);
          const formatted = points.map((p) => ({
            id: p.id,
            question: p.payload.question,
            answer: p.payload.answer,
            is_deleted: p.payload.is_deleted,
          }));

          setSearchResults(formatted);
        } else {
          setSearchResults([]);
        }
      } catch (err) {
        console.error("Search failed:", err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 1000),
  ).current;

  const handleSearchChange = (text) => {
    setSearchText(text);

    if (!text.trim()) {
      setIsSearching(false);
      setSearchResults([]);
      setSearchLoading(false);
      fetchDeleted(true);
      return;
    }

    setIsSearching(true);
    setSearchLoading(true);
    setQaList([]); // hide normal list while searching

    debouncedSearch(text);
  };

  // Update select all when selection changes
  useEffect(() => {
    const currentList = isSearching ? searchResults : qaList;
    if (currentList.length > 0 && selectedIds.length === currentList.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedIds, qaList, searchResults, isSearching]);

  const handleModalResponseForDelete = async (choice) => {
    setshowWarningModal(false);
    if (choice == "no") {
      return;
    } else if (choice == "yes") {
      permanentDelete(qAIdToDelete);
      setqAIdToDelete(null);
    }
  };
  const handleModalResponseForBulkDelete = async (choice) => {
    setshowWarningModalForBulkDelete(false);
    if (choice == "no") {
      return;
    } else if (choice == "yes") {
      handleBulkDelete();
    }
  };
  return (
    <>
      <div className="role-box">
        <h2>Deleted Q/A</h2>
        <div className="show-saved">
          <div className="form-wraper">
            <label>Search</label>
            <input
              type="text"
              className="search qa-search-input"
              placeholder="Search deleted questions..."
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            <img src={searchSvg} alt="Search" />
          </div>
          <div className="d-flex align-items-center flex-wrap gap-10">
            {!(bulkRestoreMode || bulkDeleteMode) && (
              <>
                <button
                  className="primary_btn mr_10"
                  onClick={() => setBulkRestoreMode(true)}
                >
                  <img src={restoreSvg} alt="Restore" />
                  Bulk Restore
                </button>

                <button
                  className="delete-btn"
                  onClick={() => setBulkDeleteMode(true)}
                >
                  <img src={deleteSvg} alt="Delete" />
                  Bulk Delete
                </button>
              </>
            )}
            {(bulkRestoreMode || bulkDeleteMode) && (
              <button className="cancel-btn" onClick={exitBulkMode}>
                <i className="bi bi-x-lg"></i>
                Cancel Bulk
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="qa-page">
        {/* Header */}
        <div className="qa-header">
          <h2>Deleted Q&A</h2>

          {/* Selection counter in header when in bulk mode */}
          {(bulkRestoreMode || bulkDeleteMode) && selectedIds.length > 0 && (
            <div className="qa-selection-counter deleted">
              <span className="qa-selection-count">{selectedIds.length}</span>
              <span>selected</span>
            </div>
          )}
        </div>

        {/* Bulk Operation Toolbar */}
        {(bulkRestoreMode || bulkDeleteMode) && (
          <div
            className={`qa-bulk-toolbar ${bulkDeleteMode ? "danger" : "warning"}`}
          >
            <div className="qa-bulk-toolbar-left">
              <label className="qa-select-all-label">
                <input
                  type="checkbox"
                  className="qa-checkbox"
                  checked={selectAll}
                  onChange={toggleSelectAll}
                />
                <span>Select All</span>
              </label>

              <div className="qa-selection-info">
                {selectedIds.length > 0 ? (
                  <>
                    <span className="qa-selected-count">
                      {selectedIds.length}
                    </span>
                    <span>
                      {" "}
                      of {isSearching
                        ? searchResults.length
                        : qaList.length}{" "}
                      selected
                    </span>
                  </>
                ) : (
                  <span>No items selected</span>
                )}
              </div>
            </div>

            <div className="qa-bulk-toolbar-right">
              {selectedIds.length > 0 && (
                <button
                  className="qa-btn qa-btn-clear"
                  onClick={clearSelections}
                >
                  Clear
                </button>
              )}

              {bulkRestoreMode ? (
                <button
                  className="primary_btn"
                  disabled={selectedIds.length === 0}
                  onClick={handleBulkRestore}
                >
                  <img src={restoreSvg} alt="Restore" />
                  Restore ({selectedIds.length})
                </button>
              ) : (
                <button
                  className="delete-btn"
                  disabled={selectedIds.length === 0}
                  onClick={() => {
                    setshowWarningModalForBulkDelete(true);
                  }}
                >
                  <img src={deleteSvg} alt="Delete" />
                  Delete Permanently ({selectedIds.length})
                </button>
              )}
            </div>
          </div>
        )}

        {/* SEARCH RESULTS */}
        {isSearching ? (
          <>
            {searchLoading ? (
              <div className="qa-full-loader">
                <div className="spinner"></div>
                <p>Searching questions...</p>
              </div>
            ) : searchResults.length > 0 ? (
              <div className="qa_list">
                {searchResults.map((item, idx) => (
                  <div className="d-flex align-items-center mb-20">
                    {(bulkRestoreMode || bulkDeleteMode) && (
                      <div className="qa-select-overlay">
                        <input
                          type="checkbox"
                          className="qa-checkbox qa-item-checkbox"
                          checked={selectedIds.includes(item.id)}
                          onChange={() => handleCheckboxChange(item.id)}
                        />
                      </div>
                    )}
                    <div
                      key={item.id}
                      className={`qa-card card deleted ${
                        selectedIds.includes(item.id) ? "selected" : ""
                      }`}
                    >
                      <div className="qa-card-body">
                        <div className="qa-content">
                          <p>
                            <strong>{idx + 1}. Question:</strong>{" "}
                            <Linkify options={linkifyOptions}>
                              {item.question}
                            </Linkify>
                          </p>

                          <p>
                            <strong>Answer:</strong>{" "}
                            <Linkify options={linkifyOptions}>
                              {item.answer}
                            </Linkify>
                          </p>

                          {!(bulkRestoreMode || bulkDeleteMode) && (
                            <div className="qa-actions">
                              <button
                                className="edit-btn"
                                onClick={() => openModal(item)}
                              >
                                <img src={editSvg} alt="Edit" />
                                Edit
                              </button>

                              <button
                                className="primary_btn"
                                onClick={() => restoreItem(item.id)}
                              >
                                <img src={restoreSvg} alt="Restore" />
                                Restore
                              </button>

                              <button
                                className="delete-btn"
                                onClick={() => permanentDelete(item.id)}
                              >
                                <img src={deleteSvg} alt="Delete" />
                                Permanent Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="qa-empty-state">
                <p>No results found...</p>
              </div>
            )}
          </>
        ) : (
          // NORMAL PAGINATION LIST
          <>
            {loading && qaList.length === 0 ? (
              <div className="qa-full-loader">
                <div className="spinner"></div>
                <p>Loading questions...</p>
              </div>
            ) : qaList.length > 0 ? (
              <>
                <div className="qa_list">
                  {getPaginatedData().map((item, idx) => (
                    <div className="d-flex align-items-center mb-20">
                      {(bulkRestoreMode || bulkDeleteMode) && (
                        <div className="qa-select-overlay">
                          <input
                            type="checkbox"
                            className="qa-checkbox qa-item-checkbox"
                            checked={selectedIds.includes(item.id)}
                            onChange={() => handleCheckboxChange(item.id)}
                          />
                        </div>
                      )}
                      <div
                        key={item.id}
                        className={`qa-card card deleted ${
                          selectedIds.includes(item.id) ? "selected" : ""
                        }`}
                      >
                        <div className="qa-card-body">
                          <div className="qa-content">
                            <p>
                              <strong>
                                {(currentPage - 1) * itemsPerPage + idx + 1}.
                                Question:
                              </strong>{" "}
                              <Linkify options={linkifyOptions}>
                                {item.question}
                              </Linkify>
                            </p>
                            <p>
                              <strong>Answer:</strong>{" "}
                              <Linkify options={linkifyOptions}>
                                {item.answer}
                              </Linkify>
                            </p>

                            {!(bulkRestoreMode || bulkDeleteMode) && (
                              <div className="qa-actions">
                                <button
                                  className="edit-btn"
                                  onClick={() => openModal(item)}
                                >
                                  <img src={editSvg} alt="Edit" />
                                  Edit
                                </button>

                                <button
                                  className="primary_btn"
                                  onClick={() => restoreItem(item.id)}
                                >
                                  <img src={restoreSvg} alt="Restore" />
                                  Restore
                                </button>

                                <button
                                  className="delete-btn"
                                  onClick={() => {
                                    setshowWarningModal(true);
                                    setqAIdToDelete(item.id);
                                  }}
                                >
                                  <img src={deleteSvg} alt="Delete" />
                                  Permanent Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div ref={bottomRef} style={{ height: "1px" }}></div>
              </>
            ) : (
              <div className="qa-empty-state">
                <p>No deleted Q&A found.</p>
              </div>
            )}
          </>
        )}

        {loading && qaList.length > 0 && (
          <div className="qa-loading-more">Loading more...</div>
        )}

        <div ref={bottomRef} style={{ height: "1px" }}></div>

        {/* EDIT MODAL */}
        {showModal && (
          <div className="qa-modal-overlay" onMouseDown={closeModal}>
            <div className="qa-modal" onMouseDown={(e) => e.stopPropagation()}>
              <div className="qa-modal-header">
                <h5>Edit Deleted Q&A</h5>
                <button className="qa-btn-close" onClick={closeModal}>
                  ✖
                </button>
              </div>

              <div className="qa-modal-body">
                <div className="form-wraper-bx">
                  <label className="label">Question</label>
                  <input
                    className="form-control"
                    type="text"
                    value={editForm.question}
                    onChange={(e) =>
                      setEditForm((s) => ({ ...s, question: e.target.value }))
                    }
                  />
                </div>
                <div className="form-wraper-bx">
                  <label className="label">Answer</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={editForm.answer}
                    onChange={(e) =>
                      setEditForm((s) => ({ ...s, answer: e.target.value }))
                    }
                  />
                </div>
                <div className="d-flex justify-content-center">
                  <button className="delete_approve_btn" onClick={saveEdit}>
                    Save
                  </button>
                  <button className="delete_reject_btn" onClick={closeModal}>
                    Cancel
                  </button>
                </div>
              </div>

              <div className="qa-modal-footer"></div>
            </div>
          </div>
        )}
      </div>
      {!isSearching && (
        <div className="pagination_wraper">
          <div className="d-flex align-items-center">
            <div>
              <label className="page-size">
                Page <strong>{currentPage}</strong> of{" "}
                <strong>{itemsPerPage}</strong>{" "}
              </label>
            </div>
            <div className="PageSizeChageDropdown">
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(e.target.value)}
                disabled={loading || loadingBatch}
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>

          <div className="page">
            <div
              className={`${currentPage === 1 || loading || loadingBatch ? "disabled" : "page__btn_qdrant"}`}
              onClick={handlePrevPage}
            >
              <img
                src={require("../../assets/images/arrow.png")}
                className="material-icons left_arrow"
                alt="Arrow"
              />
            </div>

            {/* Page Numbers */}
            {generatePageNumbers().map((pageNum, idx) => (
              <div
                key={idx}
                className={`page__numbers ${
                  pageNum === currentPage ? "active_page" : ""
                } ${pageNum === "..." ? "ellipsis" : ""}`}
                onClick={() => pageNum !== "..." && handlePageChange(pageNum)}
                style={{
                  cursor:
                    pageNum === "..." || loading || loadingBatch
                      ? "default"
                      : "pointer",
                  opacity: loading || loadingBatch ? 0.5 : 1,
                }}
              >
                {pageNum}
              </div>
            ))}

            <div
              className={`${currentPage === getTotalPages() || qaList.length === 0 || loading || loadingBatch ? "disabled" : "page__btn_qdrant"}`}
              onClick={handleNextPage}
            >
              <img
                src={require("../../assets/images/arrow.png")}
                className="material-icons"
                alt="Arrow"
              />
            </div>
          </div>

          {/* <div className="pagination-info">
            <span>
              {qaList.length > 0
                ? `${(currentPage - 1) * itemsPerPage + 1} - ${Math.min(
                  currentPage * itemsPerPage,
                  qaList.length,
                )} of ${qaList.length}`
                : "No data"}
            </span>
          </div> */}
        </div>
      )}

      {showWarningModal && (
        <ConfirmModal
          title="Action Required"
          message="Are you sure you want to permanently delete this question?"
          confirmText="Yes, I'm Sure"
          cancelText="Cancel"
          onClose={handleModalResponseForDelete}
        />
      )}

      {showWarningModalForBulkDelete && (
        <ConfirmModal
          title="Action Required"
          message={`Are you sure you want to delete ${selectedIds.length} ${selectedIds.length > 1 ? "questions" : "question"}?`}
          confirmText="Yes, I'm Sure"
          cancelText="Cancel"
          onClose={handleModalResponseForBulkDelete}
        />
      )}
    </>
  );
}
